import { useMemo, useState } from "react"
import { capitalize } from "lodash"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import type {
	iconButtonVariantsType,
	ProjectStatusTypes,
} from "@productlane/ui"
import type { Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	IconButton,
	Loader,
	Popover,
	ProjectStatusIcon,
	ProjectStatusIcons,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"

import { PropertyTrigger } from "@/components/property-trigger"
import { RoleCheck } from "@/components/shared/role-check"
import { projectStatusOrder } from "@/constants"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"

interface Props {
	project: Project
	iconSize?: "sm" | "default" | "lg"
	className?: string
	iconVariant?: iconButtonVariantsType
	withStatus?: boolean
}

export const projectStatusMap: Record<string, string> = {
	started: "In Progress",
	planned: "Planned",
	backlog: "Backlog",
	triage: "Triage",
	completed: "Completed",
	unstarted: "Unstarted",
	canceled: "Canceled",
	paused: "Paused",
	closed: "Closed",
} as const

const iconSizeMap = {
	sm: "h-3 w-3 min-h-3 min-w-3",
	default: "h-3.5 w-3.5 min-h-3.5 min-w-3.5",
	lg: "h-5 w-5 min-h-5 min-w-5",
}

export const ProjectStatusSelect = ({
	project,
	className,
	iconSize = "default",
	iconVariant = "ghost-brightness",
	withStatus = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const zero = useZero()
	const { role } = useRole()

	const { data: organization } = api.linear.organization.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
		enabled: isOpen,
	})

	const projectStatusOptions = useMemo(
		() =>
			(organization?.projectStatuses ?? [])
				.map(({ id, name, type, color, position }) => {
					const Icon = ProjectStatusIcons[type]
					const isSelected = project.statusId
						? project.statusId === id
						: project.stateName
							? project.stateName === name
							: project.state === type
					return {
						label: (
							<div className="ml-0.5 flex w-full items-center gap-2">
								<Icon className="h-3.5 w-3.5 shrink-0" color={color} />
								<span className="flex-1 truncate">{name}</span>
								{isSelected && <Check className="h-3 w-3 shrink-0" />}
							</div>
						),
						value: `${type}:${name}`,
						linearStatusId: id,
						type,
						name,
						color,
						position,
					}
				})
				.sort((a, b) => {
					const typeOrder =
						projectStatusOrder.indexOf(a.type) -
						projectStatusOrder.indexOf(b.type)
					if (typeOrder !== 0) return typeOrder
					return a.position - b.position
				}),
		[
			organization?.projectStatuses,
			project.statusId,
			project.state,
			project.stateName,
		],
	)

	const displayName =
		project.stateName ??
		(project.state
			? (projectStatusMap[project.state] ?? capitalize(project.state))
			: "")

	return (
		<>
			<RoleCheck not={["VIEWER"]}>
				<Popover
					onOpenChange={setIsOpen}
					open={isOpen}
					modal={false}
					className="p-0"
					content={
						projectStatusOptions.length === 0 ? (
							<div className="flex items-center justify-center py-8">
								<Loader />
							</div>
						) : (
							<Command
								loop
								filter={(_, search, keywords) =>
									keywords?.find((keyword) =>
										keyword
											.toLocaleLowerCase()
											.includes(search.toLocaleLowerCase()),
									)
										? 1
										: 0
								}
							>
								<CommandInput autoFocus placeholder="Change status..." />
								<CommandList>
									<CommandEmpty>No matching states</CommandEmpty>
									<CommandGroup>
										{projectStatusOptions.map(
											(
												{ label, value, linearStatusId, type, name, color },
												index,
											) => (
												<CommandItem
													keywords={[name, type]}
													key={`project-status-${index}`}
													value={value}
													onSelect={async () => {
														await mutateOk(
															zero.mutate(
																mutators.projects.update({
																	id: project.id,
																	state: type,
																	stateName: name,
																	stateColor: color,
																	linearStatusId,
																}),
															),
														)
														analytics.track("project-status-update")
														setIsOpen(false)
													}}
												>
													<div className="text-md flex w-full items-center justify-between -space-y-0.5">
														<div className="flex w-full truncate">
															<div className="w-full flex-none font-medium">
																{label}
															</div>
														</div>
													</div>
												</CommandItem>
											),
										)}
									</CommandGroup>
								</CommandList>
							</Command>
						)
					}
				>
					{withStatus ? (
						<PropertyTrigger
							tooltip="Change status"
							className={className}
							icon={
								<ProjectStatusIcon
									className="h-3.5 w-3.5 flex-none overflow-visible"
									status={project.state as ProjectStatusTypes}
									customColor={project.stateColor ?? undefined}
									colored
								/>
							}
						>
							{displayName}
						</PropertyTrigger>
					) : (
						<div className={cn("group", className)}>
							<Tooltip label="Change status" shouldWrapChildren>
								<div className="flex flex-row items-center gap-1">
									<IconButton size="md" variant={iconVariant}>
										<ProjectStatusIcon
											className={cn(
												"overflow-visible transition-all",
												iconSizeMap[iconSize],
											)}
											status={project.state as ProjectStatusTypes}
											customColor={project.stateColor ?? undefined}
											colored
										/>
									</IconButton>
								</div>
							</Tooltip>
						</div>
					)}
				</Popover>
			</RoleCheck>
			{role === "VIEWER" &&
				(withStatus ? (
					<PropertyTrigger
						className={className}
						icon={
							<ProjectStatusIcon
								className="h-3.5 w-3.5 flex-none overflow-visible"
								status={project.state as ProjectStatusTypes}
								customColor={project.stateColor ?? undefined}
								colored
							/>
						}
					>
						{displayName}
					</PropertyTrigger>
				) : (
					<ProjectStatusIcon
						className={cn("overflow-visible", iconSizeMap[iconSize])}
						status={project.state as ProjectStatusTypes}
						customColor={project.stateColor ?? undefined}
						colored
					/>
				))}
		</>
	)
}
